import {
  Card,
  CardCaption,
  EducationScreen,
  Gold,
  RevealBlock,
  ScreenBody,
  ScreenIntro,
  ScreenSubhead,
} from './EducationShell';
import {StaticStepList, StepperCarouselViz, type Step11Brand, type Step11Step} from './Step11Viz';
import styles from './Glp1EducationLegitimacyCompounded.module.css';

const STEPS: Step11Step[] = [
  {
    title: 'A real clinical screening',
    short: 'Screening',
    detail:
      "Same questions a sleep-medicine doctor would ask in clinic, so you only get prescribed what's right for you. Validated against in-lab polysomnography.",
  },
  {
    title: 'A licensed clinician reviews your case',
    short: 'Review',
    detail: 'They titrate your dose for sleep apnea. Not an algorithm, not a checkbox form.',
  },
  {
    title: "You only pay if it's the right fit",
    short: 'Right fit',
    detail:
      "If your clinician decides this isn't right for you, you get a full refund automatically. No proof, no paperwork.",
  },
  {
    title: 'Your medication arrives safely',
    short: 'Delivery',
    detail: 'Your sublingual semaglutide drops are compounded by a licensed pharmacy.',
  },
  {
    title: 'Unlimited access to RISE Care Team (clinician and licensed pharmacist)',
    short: 'Support',
    detail:
      'Message your provider or pharmacist anytime with questions about dosing, side effects, or anything that comes up. You can call a licensed pharmacist directly. Anytime.',
  },
];

const BRAND: Step11Brand = {
  rxName: 'Compounded Semaglutide',
  rxDosage: 'Daily oral · titrating schedule',
  pharmacy: 'Pharmacy',
  deliveryLabel: 'Monthly refill',
};

/** Step 11 — compounded. Same 5-step process but softens "recovery" to
 *  "progress" throughout. No FDA references (compounding pharmacies are
 *  state-licensed, not FDA-approved). */
export const Glp1EducationLegitimacyCompounded = () => (
  <EducationScreen
    ctaLabel="Next"
    variant="compounded_v1"
    analyticsShown="GLP-1 Funnel Education Legitimacy Viewed"
    analyticsSubmitted="GLP-1 Funnel Education Legitimacy Continue Tapped"
  >
    <ScreenIntro
      headline={
        <>
          A real clinician. A real prescription. <Gold>Real progress.</Gold>
        </>
      }
      subhead="RISE Expert Care brings a daily sleep practice and medical care together in one place."
    />

    <RevealBlock delayMs={300}>
      <Card>
        <StepperCarouselViz steps={STEPS} brand={BRAND} />
        <CardCaption>Your case is reviewed by a real clinician before anything ships.</CardCaption>
      </Card>
    </RevealBlock>

    <RevealBlock delayMs={500}>
      <StaticStepList
        items={STEPS}
        title={
          <h3 className={styles.listHeading}>
            A <Gold>licensed clinician</Gold> guides every step
          </h3>
        }
      />
    </RevealBlock>

    <RevealBlock delayMs={500}>
      <ScreenSubhead>The complete solution</ScreenSubhead>
      <ScreenBody>
        RISE Expert Care is the comprehensive solution for sleep apnea: a daily sleep practice through the RISE app,
        plus medication for the airway physiology that habits alone can&apos;t fix, plus a real clinician you can
        message. Each link in the chain is independent. None of them sells you the next. If the medication isn&apos;t
        right for you, you get a full refund automatically.
      </ScreenBody>
    </RevealBlock>
  </EducationScreen>
);

export default Glp1EducationLegitimacyCompounded;
